import axios, { AxiosInstance, AxiosResponse } from 'axios';
import DEPLOYMENT_CONFIG from '../config/deployment';
import { getUserTenant } from '../utils/tenantUtils';

// API base configuration - Use deployment config
const API_BASE_URL = DEPLOYMENT_CONFIG.getBackendUrl();

// Helper function to get tenant-aware API URL
const getTenantAwareUrl = (url: string): string => {
  const tenant = getUserTenant();
  // If partnership tenant and URL starts with /api, prepend /partnership
  if (tenant === 'partnership' && url.startsWith('/api/')) {
    return `/partnership${url}`;
  }
  return url;
};

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased timeout to 30 seconds for complex analytics queries
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for CSRF
});

// CSRF token cache
let csrfToken: string | null = null;

// Token refresh lock to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Function to get CSRF token
const getCsrfToken = async (): Promise<string | null> => {
  if (csrfToken) {
    return csrfToken;
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/api/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.csrf_token;
    return csrfToken;
  } catch (error) {
    return null;
  }
};

// Request interceptor to add JWT token, CSRF token, and make URLs tenant-aware
api.interceptors.request.use(
  async (config) => {
    // Make URL tenant-aware
    if (config.url) {
      config.url = getTenantAwareUrl(config.url);
    }
    
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Log warning if token is missing for authenticated endpoints
      if (config.url && !config.url.includes('/login') && !config.url.includes('/csrf-token')) {
        console.warn('JWT token missing for request:', config.url);
      }
    }

    // Add CSRF token for POST, PUT, DELETE requests
    if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '')) {
      const csrf = await getCsrfToken();
      if (csrf) {
        config.headers['X-CSRFToken'] = csrf;
      }
    }

    // Multipart must include a boundary; let the browser set Content-Type for FormData
    if (config.data instanceof FormData) {
      const h = config.headers as Record<string, unknown> & { delete?: (name: string) => void };
      if (typeof h?.delete === 'function') {
        h.delete('Content-Type');
      } else {
        delete h['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and CSRF errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    // Handle CSRF token errors
    if (error.response?.status === 400 && error.response?.data?.message?.includes('CSRF')) {
      // Clear cached CSRF token and retry
      csrfToken = null;
      const originalRequest = error.config;
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        const csrf = await getCsrfToken();
        if (csrf) {
          originalRequest.headers['X-CSRFToken'] = csrf;
        }
        return api(originalRequest);
      } else {
        // If retry already attempted or failed, logout automatically

        // Clear all authentication data
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('user');
        localStorage.removeItem('login_token');
        localStorage.removeItem('login_username');

        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    if (error.response?.status === 401) {
      // Token expired or invalid
      // Check if this is a retry to avoid infinite loops
      const originalRequest = error.config;
      if (originalRequest && originalRequest._retry) {
        // Already tried to refresh, don't try again
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('user');
        localStorage.removeItem('login_token');
        localStorage.removeItem('login_username');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // If JWT token was just set (within last 5 seconds), don't try to refresh - something else is wrong
      const jwtTokenSetTimeStr = localStorage.getItem('jwt_token_set_time');
      if (jwtTokenSetTimeStr) {
        const jwtTokenSetTime = parseInt(jwtTokenSetTimeStr, 10);
        const now = Date.now();
        if ((now - jwtTokenSetTime) < 5000) {
          console.warn('401 error immediately after setting JWT token - token may not be valid or there may be a backend issue');
          // Don't try to refresh, just redirect to login
          localStorage.removeItem('jwt_token');
          localStorage.removeItem('jwt_token_set_time');
          localStorage.removeItem('cognito_access_token');
          localStorage.removeItem('cognito_id_token');
          localStorage.removeItem('cognito_refresh_token');
          localStorage.removeItem('cognito_username');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      }

      // Check if a refresh is already in progress
      if (isRefreshing && refreshPromise) {
        // Wait for the ongoing refresh to complete
        try {
          const newToken = await refreshPromise;
          if (newToken && originalRequest) {
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, will fall through to logout
        }
      }

      // Check if user has Cognito refresh token (Cognito user)
      const cognitoRefreshToken = localStorage.getItem('cognito_refresh_token');
      const cognitoUsername = localStorage.getItem('cognito_username');
      
      if (cognitoRefreshToken && cognitoUsername) {
        // Start refresh process (only if not already refreshing)
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = (async (): Promise<string | null> => {
            try {
              const csrf = await getCsrfToken();
              // Use tenant-aware URL
              const refreshUrl = getTenantAwareUrl('/api/cognito/refresh');
              const refreshResponse = await axios.post(`${API_BASE_URL}${refreshUrl}`, {
                refresh_token: cognitoRefreshToken,
                username: cognitoUsername
              }, {
                headers: csrf ? { 'X-CSRFToken': csrf } : {},
                withCredentials: true
              });

              if (refreshResponse.data.success && refreshResponse.data.jwt_token) {
                // Store new JWT token
                localStorage.setItem('jwt_token', refreshResponse.data.jwt_token);
                
                // Also update Cognito tokens if provided
                if (refreshResponse.data.access_token) {
                  localStorage.setItem('cognito_access_token', refreshResponse.data.access_token);
                }
                if (refreshResponse.data.id_token) {
                  localStorage.setItem('cognito_id_token', refreshResponse.data.id_token);
                }

                return refreshResponse.data.jwt_token;
              } else {
                console.warn('Token refresh failed: No JWT token in response', refreshResponse.data);
                throw new Error('No JWT token in refresh response');
              }
            } catch (cognitoRefreshError: any) {
              console.error('Cognito token refresh error:', cognitoRefreshError);
              // If refresh fails with specific error codes, clear tokens
              const errorMessage = cognitoRefreshError.response?.data?.message || '';
              const errorCode = cognitoRefreshError.response?.data?.error_code;
              
              if (errorCode === 'REQUIRES_FRESH_LOGIN' || 
                  errorMessage.includes('Invalid Refresh Token') ||
                  errorMessage.includes('NotAuthorizedException')) {
                // Clear all tokens - refresh token is invalid, user needs to login again
                localStorage.removeItem('jwt_token');
                localStorage.removeItem('cognito_access_token');
                localStorage.removeItem('cognito_id_token');
                localStorage.removeItem('cognito_refresh_token');
                localStorage.removeItem('cognito_username');
                localStorage.removeItem('user');
                localStorage.removeItem('login_token');
                localStorage.removeItem('login_username');
              }
              throw cognitoRefreshError;
            } finally {
              isRefreshing = false;
              refreshPromise = null;
            }
          })();
        }

        // Wait for refresh to complete
        try {
          const newToken = await refreshPromise;
          if (newToken && originalRequest) {
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        } catch (refreshError: any) {
          // Refresh failed, check if it's an invalid refresh token
          const errorMessage = refreshError.response?.data?.message || '';
          const errorCode = refreshError.response?.data?.error_code;
          
          if (errorCode === 'REQUIRES_FRESH_LOGIN' || 
              errorMessage.includes('Invalid Refresh Token') ||
              errorMessage.includes('NotAuthorizedException')) {
            // Refresh token is invalid, redirect to login immediately
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }
          // Otherwise fall through to logout below
        }
      } else {
        // Not a Cognito user, try admin refresh (for backward compatibility)
        const currentToken = localStorage.getItem('jwt_token');
        if (currentToken) {
          try {
            const refreshResponse = await api.post('/api/admin/refresh-token', {}, {
              headers: { Authorization: `Bearer ${currentToken}` }
            });

            if (refreshResponse.data.success) {
              const newToken = refreshResponse.data.access_token;
              localStorage.setItem('jwt_token', newToken);

              // Retry the original request with new token
              if (originalRequest) {
                originalRequest._retry = true;
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
              }
            }
          } catch (refreshError) {
            // Token refresh failed
          }
        }
      }

      // If refresh fails or no token, clear everything and redirect
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('user');
      localStorage.removeItem('login_token');
      localStorage.removeItem('login_username');
      localStorage.removeItem('cognito_access_token');
      localStorage.removeItem('cognito_id_token');
      localStorage.removeItem('cognito_refresh_token');
      localStorage.removeItem('cognito_username');

      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  business_area?: string;
  team_id?: number;
  avatar_url?: string;
  display_name?: string;
  cognito_enabled?: boolean;
  cognito_sub?: string;
  given_name?: string;
  family_name?: string;
}

export interface Lead {
  id: number;
  name: string;
  email?: string;  // Made optional for import scenarios
  phone?: string;
  company?: string;
  business_area: string;
  customer_type: string;
  lead_source?: string;
  lead_status?: string | null;
  section?: string;
  dif?: string;
  assigned_to?: number;
  created_by: number;
  product_type?: string;
  additional_cards?: string;
  assigned_user_name?: string;
  created_user_name?: string;
  created_at: string;
  updated_at: string;
  installation_date?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  current_provider?: string;
  contract_end_date?: string;
  monthly_spend?: number;
  service_interest?: string;
  last_contact_date?: string;
  next_follow_up?: string;
  lead_score?: number;
  priority?: string;
  lead_category?: number;
  tags?: string;
  pendente_reason?: string | null;
  is_duplicate?: boolean;
  duplicate_of_lead_id?: number;
  duplicate_status?: string;
  followup_note?: string | null;
  seminteresse_note?: string | null;
  informacao_errada_note?: string | null;  // Informação Errada field
  sem_cobertura_de?: string | null;  // Sem Cobertura field
  end_of_contract?: string | null;  // Fim da fidelização field
  last_contact_attempt?: string | null;  // Última tentativa de Contacto Falhada field
  contact_attempt_count?: number;  // Number of times "Set" button has been pressed
  instalados_business_area?: string;  // '1', '2' (Telecomunicação), '3' (Segurança), '4' (Energia)
  instalados_selected_products?: string;  // JSON array of product IDs: "[1, 2, 3]"
  _displayOrder?: number; // Internal field to maintain backend ordering
}

export type LeadFileReviewStatus = 'pending' | 'approved' | 'rejected';

export interface LeadFileReviewAudit {
  id: number | string;
  lead_file_id: number;
  reviewer_id?: number;
  reviewer_name?: string;
  action: 'approved' | 'rejected';
  note?: string;
  rejection_reason?: string;
  created_at: string;
}

export interface LeadFileReviewItem {
  id: number | string;
  item_type?: 'file' | 'form';
  lead_id: number;
  lead?: Lead;
  file_name: string;
  file_size?: number;
  file_type?: string;
  stage?: string;
  field_name?: string;
  business_area?: string;
  uploaded_at: string;
  user_name?: string;
  is_required?: boolean;
  review_status: LeadFileReviewStatus;
  reviewed_at?: string | null;
  reviewer_name?: string | null;
  form_type?: string;
  form_label?: string;
}

export interface Activity {
  id: number;
  title: string;
  activity_type: string;
  priority: string;
  status: string;
  description?: string;
  due_date?: string;
  completed_date?: string;
  duration_minutes?: number;
  lead_id?: number;
  account_id?: number;
  contact_id?: number;
  deal_id?: number;
  assigned_to?: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  subject?: string;
  outcome?: string;
  next_action?: string;
  reminder_enabled?: boolean;
  reminder_time?: string;
  notes?: string;
  tags?: string;
  importance_level?: number;
  persons_of_interest?: number[];
}

export interface Team {
  id: number;
  name: string;
  parent_id?: number;
  team_leader_id?: number;
  members: User[];
  children: Team[];
  description?: string;
  business_area?: string;
  created_at?: string;
  updated_at?: string;
  is_active?: boolean;
  member_count?: number;
  children_count?: number;
}

export interface Notification {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  priority: string;
  read: boolean;
  created_at: string;
  expires_at: string;
  is_expired?: boolean;
  task_id?: number;
  lead_id?: number;
  account_id?: number;
  deal_id?: number;
  activity_id?: number;
  action_url?: string;
  meta_data?: any;
}

export interface AssignmentHistory {
  id: number;
  lead_id: number;
  previous_assigned_to?: number;
  assigned_to: number;
  assigned_by: number;
  assigned_at: string;
}

// Vodafone Document Generation Types
export interface VodafoneTemplate {
  name: string;
  filename: string;
  display_name: string;
}

export interface VodafoneField {
  name: string;
  type: string;
  required: boolean;
  position?: { x: number; y: number; width?: number; height?: number };
  templateName?: string;  // Added for multiple template support
  originalName?: string;  // Added for multiple template support
  label?: string; // User-friendly label for display
  options?: string[]; // Options for dropdown fields (/Ch type)
  group?: string; // Field grouping (e.g., 'operadora_origem', 'data_hora')
  validation?: {
    type: string;
    pattern: string;
    message?: string;
  };
  export_value?: string; // Export value for checkbox fields
  related_field?: string; // Related field from another template for auto-fill
}

export interface VodafoneDocument {
  id: number;
  lead_id: number;
  user_id: number;
  document_type: string;
  file_path: string;
  field_data: Record<string, any>;
  created_at: string;
  expires_at: string;
  is_expired: boolean;
  days_until_expiry: number;
}

// API Service Class
class ApiService {
  // Authentication
  async login(username: string, password: string): Promise<{ success?: boolean; token?: string; access_token?: string; user?: User; requires_totp?: boolean; login_token?: string; message?: string; tenant?: string }> {
    try {
      const response = await api.post('/api/login', { username, password });
      const { success, access_token, token, user, requires_totp, login_token, message, tenant } = response.data;

      // Handle both access_token and token for backward compatibility
      const finalToken = access_token || token;

      if (finalToken && user) {
        localStorage.setItem('jwt_token', finalToken);
        localStorage.setItem('user', JSON.stringify(user));
      }

      return { success, token: finalToken, user, requires_totp, login_token, message, tenant };
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user');
    localStorage.removeItem('login_token');
    localStorage.removeItem('login_username');
    // Clear CSRF token cache
    csrfToken = null;
  }

  async getCsrfToken(): Promise<string | null> {
    return await getCsrfToken();
  }

  async refreshToken(): Promise<{ success?: boolean; token?: string; user?: User; message?: string }> {
    try {
      const response = await api.post('/api/admin/refresh-token');
      const { success, access_token, user, message } = response.data;

      if (access_token && user) {
        localStorage.setItem('jwt_token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
      }

      return { success, token: access_token, user, message };
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async verifyTOTP(username: string, loginToken: string, totpCode: string): Promise<{ success?: boolean; token?: string; access_token?: string; user?: User; message?: string; tenant?: string }> {
    try {
      const response = await api.post('/api/admin/login/verify', {
        username,
        login_token: loginToken,
        totp_code: totpCode
      });

      // Handle both access_token and token for backward compatibility
      const { success, access_token, token, user, message, tenant } = response.data;
      const finalToken = access_token || token;

      if (finalToken && user) {
        localStorage.setItem('jwt_token', finalToken);
        localStorage.setItem('user', JSON.stringify(user));
      }

      return { success, token: finalToken, user, message, tenant };
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  // Users
  async getUsers(): Promise<User[]> {
    const response = await api.get('/api/users');
    return response.data.users;
  }

  async getAssignableUsers(): Promise<User[]> {
    const response = await api.get('/api/leads/assignable-users');
    return response.data.users;
  }

  async getViewableUsers(): Promise<User[]> {
    const response = await api.get('/api/leads/viewable-users');
    return response.data.users;
  }

  async getTeamLeaders(): Promise<Array<{ id: number; username: string; email: string; role: string; team_id: number | null; team_name: string | null; member_count: number }>> {
    const response = await api.get('/api/team-validation/team-leaders');
    return response.data.team_leaders;
  }

  async getTeamValidationData(teamLeaderId?: number): Promise<{
    success: boolean;
    validation_leads: Array<any>;
    nos_contratos: Array<any>;
    altice_forms: Array<any>;
    energy_proposals: Array<any>;
    prosegur_forms: Array<any>;
    team_members: Array<any>;
    team_leader: { id: number; username: string; role: string };
  }> {
    const params = teamLeaderId ? { team_leader_id: teamLeaderId } : {};
    const response = await api.get('/api/team-validation/data', { params });
    return response.data;
  }

  async createUser(userData: Partial<User>): Promise<{ success: boolean; message: string; user?: User }> {
    const response = await api.post('/api/users', userData);
    return response.data;
  }

  async updateUser(id: number, userData: Partial<User>): Promise<{ success: boolean; message: string; user?: User }> {
    const response = await api.put(`/api/users/${id}`, userData);
    return response.data;
  }

  async updateCurrentUser(userData: Partial<User>): Promise<{ success: boolean; message: string; user?: User }> {
    try {
      const response = await api.put('/api/users/me', userData);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async deleteUser(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/api/users/${id}`);
    return response.data;
  }

  async resendUserInvitation(userId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/api/users/${userId}/resend-invitation`);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const requestData: any = {
        new_password: newPassword
      };

      // Only include current_password if it's provided
      if (currentPassword && currentPassword.trim() !== '') {
        requestData.current_password = currentPassword;
      }

      const response = await api.put('/api/users/me', requestData);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  // Avatar upload
  async uploadAvatar(file: File): Promise<{ success: boolean; avatar_url?: string; message?: string }> {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await api.post('/api/upload-avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Leads
  async getLeads(business_area?: string, user_id?: number): Promise<Lead[]> {
    const params: any = {};
    if (business_area) params.business_area = business_area;
    if (user_id) params.user_id = user_id;
    const response = await api.get('/api/leads', { params });
    return response.data;
  }

  async getLead(id: number): Promise<Lead> {
    const response = await api.get(`/api/leads/${id}`);
    return response.data;
  }

  async createLead(leadData: Partial<Lead>): Promise<Lead> {
    const response = await api.post('/api/leads', leadData);
    const data = response.data;
    if (data && typeof data === 'object' && 'lead' in data && (data as any).lead) {
      return (data as any).lead as Lead;
    }
    return data as Lead;
  }

  async updateLead(id: number, leadData: Partial<Lead>): Promise<Lead> {
    const response = await api.put(`/api/leads/${id}`, leadData);
    const data = response.data;
    if (data && typeof data === 'object' && 'lead' in data && (data as any).lead) {
      return (data as any).lead as Lead;
    }
    return data as Lead;
  }

  async batchAssignLeads(leadIds: number[], assignedTo: number, assignedBy: number): Promise<any> {
    const response = await api.post('/api/leads/batch-assign', {
      lead_ids: leadIds,
      assigned_to: assignedTo,
      assigned_by: assignedBy
    });
    return response.data;
  }

  async deleteLead(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/api/leads/${id}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async getAssignmentHistory(leadId: number): Promise<AssignmentHistory[]> {
    const response = await api.get(`/api/leads/${leadId}/assignment-history`);
    return response.data.assignment_history;
  }

  // Lead Import
  async importTelecomLeads(file: File, businessArea?: string, leadCategory?: number): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (businessArea) {
        formData.append('business_area', businessArea);
      }
      if (leadCategory !== undefined) {
        formData.append('lead_category', leadCategory.toString());
      }
      const response = await api.post('/api/leads/import/telecom', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async importLeadsFormat(file: File, businessArea?: string, leadCategory?: number): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (businessArea) {
        formData.append('business_area', businessArea);
      }
      if (leadCategory !== undefined) {
        formData.append('lead_category', leadCategory.toString());
      }
      const response = await api.post('/api/leads/import/leads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async importEnergyLeads(file: File, leadCategory?: number): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (leadCategory !== undefined) {
      formData.append('lead_category', leadCategory.toString());
    }
    const response = await api.post('/api/leads/import/energy', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async importIntraFormat(file: File, businessArea?: string, leadCategory?: number): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (businessArea) {
        formData.append('business_area', businessArea);
      }
      if (leadCategory !== undefined) {
        formData.append('lead_category', leadCategory.toString());
      }

      const response = await api.post('/api/leads/import/intra', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async saveImportedLeads(importType: 'telecom' | 'energy', leads: any[]): Promise<any> {
    const response = await api.post(`/leads/import/${importType}/save`, { leads });
    return response.data;
  }

  // Duplicate Lead Management
  async getDuplicateLeads(): Promise<any> {
    const response = await api.get('/api/leads/duplicates');
    return response.data;
  }

  async resolveDuplicateLead(duplicateId: number, action: 'edit' | 'delete' | 'merge', data?: any): Promise<any> {
    const response = await api.post(`/api/leads/duplicates/${duplicateId}/resolve`, {
      action,
      ...(action === 'edit' && { update_data: data }),
      ...(action === 'merge' && { merge_data: data })
    });
    return response.data;
  }

  async getDuplicateStats(): Promise<any> {
    const response = await api.get('/api/leads/duplicates/stats');
    return response.data;
  }

  // Import Audit Logs
  async getImportAuditLogs(): Promise<any> {
    const response = await api.get('/api/leads/import-audit-logs');
    return response.data;
  }

  async getDuplicatesAndAudit(): Promise<any> {
    const response = await api.get('/api/leads/duplicates-and-audit');
    return response.data;
  }

  async clearImportHistory(): Promise<any> {
    try {
      const response = await api.delete('/api/leads/clear-import-history');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Activities/Calendar
  async getActivities(userId?: number, teamId?: number): Promise<Activity[]> {
    const params: any = {};
    if (userId) params.user_id = userId;
    if (teamId) params.team_id = teamId;
    const response = await api.get('/api/activities', { params });
    return response.data.activities;
  }

  async getActivity(id: number): Promise<Activity> {
    const response = await api.get(`/api/activities/${id}`);
    return response.data.activity;
  }

  async createActivity(activityData: Partial<Activity>): Promise<Activity> {
    const response = await api.post('/api/activities', activityData);
    if (response.data.success) {
      return response.data.activity;
    } else {
      throw new Error(response.data.message || 'Failed to create activity');
    }
  }

  async updateActivity(id: number, activityData: Partial<Activity>): Promise<Activity> {
    const response = await api.put(`/api/activities/${id}`, activityData);
    return response.data.activity;
  }

  async deleteActivity(id: number): Promise<void> {
    await api.delete(`/api/activities/${id}`);
  }

  async getTeamCalendar(teamId: number): Promise<any> {
    const response = await api.get(`/api/teams/${teamId}/calendar`);
    return response.data;
  }

  async syncToExternalCalendar(userId: number, activityIds: number[]): Promise<any> {
    const response = await api.post('/api/calendar/sync-external', {
      user_id: userId,
      activity_ids: activityIds
    });
    return response.data;
  }

  async getIcalCalendar(userId: number): Promise<Blob> {
    const response = await api.get(`/api/calendar/ical/${userId}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getWebcalCalendar(userId: number): Promise<string> {
    const response = await api.get(`/api/calendar/webcal/${userId}`);
    return response.data;
  }

  // Teams
  async getTeams(): Promise<Team[]> {
    const response = await api.get('/api/teams');
    const teamsTree = response.data.teams || [];

    // Flatten the hierarchical structure to show all teams
    const flattenTeams = (teams: Team[]): Team[] => {
      let flatTeams: Team[] = [];
      for (const team of teams) {
        flatTeams.push(team);
        if (team.children && team.children.length > 0) {
          flatTeams = flatTeams.concat(flattenTeams(team.children));
        }
      }
      return flatTeams;
    };

    return flattenTeams(teamsTree);
  }

  async getTeam(id: number): Promise<Team> {
    const response = await api.get(`/api/teams/${id}`);
    return response.data.team;
  }

  async createTeam(teamData: Partial<Team>): Promise<Team> {
    const response = await api.post('/api/teams', teamData);
    return response.data.team;
  }

  async updateTeam(id: number, teamData: Partial<Team>): Promise<Team> {
    const response = await api.put(`/api/teams/${id}`, teamData);
    return response.data.team;
  }

  async deleteTeam(id: number): Promise<void> {
    await api.delete(`/api/teams/${id}`);
  }

  async getTeamMembers(teamId: number): Promise<User[]> {
    const response = await api.get(`/api/teams/${teamId}/members`);
    return response.data.members || [];
  }

  async addTeamMember(teamId: number, userId: number): Promise<void> {
    await api.post(`/api/teams/${teamId}/members`, { user_id: userId });
  }

  async removeTeamMember(teamId: number, userId: number): Promise<void> {
    await api.delete(`/api/teams/${teamId}/members/${userId}`);
  }

  // Notifications
  async getNotifications(userId: number): Promise<Notification[]> {
    const response = await api.get(`/api/notifications?user_id=${userId}`);
    return response.data.notifications;
  }

  async markNotificationAsRead(id: number): Promise<void> {
    await api.post(`/api/notifications/${id}/read`);
  }

  async markAllNotificationsAsRead(userId: number): Promise<void> {
    await api.post('/api/notifications/read-all', { user_id: userId });
  }

  async deleteNotification(id: number): Promise<void> {
    await api.delete(`/api/notifications/${id}`);
  }

  async getNotificationStats(userId: number): Promise<any> {
    const response = await api.get(`/api/notifications/stats?user_id=${userId}`);
    return response.data;
  }

  // Dashboard
  async getDashboardStats(userId: number, businessArea?: string): Promise<any> {
    const params: any = { user_id: userId };
    if (businessArea) params.business_area = businessArea;
    const response = await api.get('/api/dashboard/stats', { params });
    return response.data;
  }

  async getRecentLeads(userId: number, limit: number = 10): Promise<Lead[]> {
    const response = await api.get(`/api/dashboard/recent-leads?user_id=${userId}&limit=${limit}`);
    return response.data;
  }

  async getRecentNotifications(userId: number, limit: number = 10): Promise<Notification[]> {
    const response = await api.get(`/api/dashboard/recent-notifications?user_id=${userId}&limit=${limit}`);
    return response.data;
  }

  // Admin Analytics API methods
  async getAdminOverview(params?: { user_id: number; start_date?: string; end_date?: string }): Promise<any> {
    const response = await api.get('/api/admin/analytics/overview', { params });
    return response.data;
  }

  async getUserPerformance(params: { user_id: number; target_user_id: number; start_date?: string; end_date?: string }): Promise<any> {
    const response = await api.get('/api/admin/analytics/user-performance', { params });
    return response.data;
  }

  async getTeamPerformance(params: { user_id: number; team_id: number; start_date?: string; end_date?: string }): Promise<any> {
    const response = await api.get('/api/admin/analytics/team-performance', { params });
    return response.data;
  }

  async getLeadsTable(params: {
    user_id: number;
    page?: number;
    per_page?: number;
    status?: string;
    assigned_to?: number;
    created_by?: number;
    created_date?: string;
    updated_date?: string;
    business_area?: string;
    customer_type?: string;
    section?: string;
    current_provider?: string;
    city?: string;
    sem_cobertura_de?: string;
    name?: string;
    email?: string;
    phone?: string;
    company?: string;
  }): Promise<any> {
    const response = await api.get('/api/admin/analytics/leads-table', { params });
    return response.data;
  }

  async getUsersForAnalysis(params: { user_id: number }): Promise<any> {
    const response = await api.get('/api/admin/analytics/users', { params });
    return response.data;
  }

  async getTeamsForAnalysis(params: { user_id: number }): Promise<any> {
    const response = await api.get('/api/admin/analytics/teams', { params });
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/api/users/me');
    return response.data.user;
  }

  // Advanced Lead Management - Notes
  async getLeadNotes(leadId: number): Promise<{ notes: any[] }> {
    const response = await api.get(`/api/leads/${leadId}/notes`);
    return response.data;
  }

  async createLeadNote(leadId: number, data: { title: string; content: string; importance_level: string; visibility: string }): Promise<{ note: any }> {
    const response = await api.post(`/api/leads/${leadId}/notes`, data);
    return response.data;
  }

  // Lead Files
  async getLeadFiles(leadId: number, params?: { stage?: string; field_name?: string }): Promise<{ success: boolean; files: any[] }> {
    const response = await api.get(`/api/leads/${leadId}/files`, { params });
    return response.data;
  }
  async uploadLeadFile(leadId: number, formData: FormData, onProgress?: (progress: number) => void): Promise<{ success: boolean; file?: any; message?: string }> {
    const response = await api.post(`/api/leads/${leadId}/files`, formData, {
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  }
  async deleteLeadFile(leadId: number, fileId: number): Promise<{ success: boolean; message?: string }> {
    const response = await api.delete(`/api/leads/${leadId}/files/${fileId}`);
    return response.data;
  }

  async previewLeadFile(leadId: number, fileId: number): Promise<{ success: boolean; pdf_data: string } | { success: boolean; error: string; filename?: string; file_size?: number }> {
    try {
      const response = await api.get(`/api/leads/${leadId}/files/${fileId}/preview-base64`);
      return response.data;
    } catch (error: any) {
      return { success: false, error: 'Erro ao carregar pré-visualização' };
    }
  }

  async downloadLeadFile(leadId: number, fileId: number): Promise<{ success: boolean; data: Blob }> {
    try {
      const response = await api.get(`/api/leads/${leadId}/files/${fileId}/download`, { responseType: 'blob' });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, data: new Blob() };
    }
  }

  async getFileReviewPermissions(): Promise<{ success: boolean; can_review_files: boolean; message?: string }> {
    try {
      const response = await api.get('/api/files/review/permissions');
      return response.data;
    } catch {
      return { success: false, can_review_files: false };
    }
  }

  async getFilesReviewQueue(params: {
    status?: LeadFileReviewStatus | 'all';
    search?: string;
    business_area?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    success: boolean;
    items: LeadFileReviewItem[];
    counts: Record<string, number>;
    pagination: { page: number; page_size: number; total: number };
    message?: string;
  }> {
    try {
      const response = await api.get('/api/files/review/queue', { params });
      return response.data;
    } catch {
      return {
        success: false,
        items: [],
        counts: {},
        pagination: {
          page: params.page || 1,
          page_size: params.page_size || 25,
          total: 0,
        },
        message: 'Erro ao carregar fila de revisão.',
      };
    }
  }

  async getLeadFileReviewHistory(
    leadId: number,
    fileId: number
  ): Promise<{ success: boolean; history: LeadFileReviewAudit[]; message?: string }> {
    try {
      const response = await api.get(`/api/leads/${leadId}/files/${fileId}/review-history`);
      return response.data;
    } catch {
      return { success: false, history: [], message: 'Erro ao carregar histórico.' };
    }
  }

  async reviewLeadFile(
    leadId: number,
    fileId: number,
    data: { action: 'approve' | 'reject'; note?: string; rejection_reason?: string }
  ): Promise<{ success: boolean; file?: LeadFileReviewItem; message?: string }> {
    try {
      const response = await api.post(`/api/leads/${leadId}/files/${fileId}/review`, data);
      return response.data;
    } catch {
      return { success: false, message: 'Erro ao registar decisão do ficheiro.' };
    }
  }

  async reviewLeadFilesBulk(data: {
    file_ids: number[];
    action: 'approve' | 'reject';
    note?: string;
    rejection_reason?: string;
  }): Promise<{ success: boolean; requested: number; processed: number; message?: string }> {
    try {
      const response = await api.post('/api/files/review/bulk', data);
      return response.data;
    } catch {
      return {
        success: false,
        requested: data.file_ids.length,
        processed: 0,
        message: 'Erro ao processar ficheiros em lote.',
      };
    }
  }

  async reviewLeadForm(
    leadId: number,
    formType: string,
    data: { action: 'approve' | 'reject'; note?: string; rejection_reason?: string }
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.post(`/api/leads/${leadId}/forms/${formType}/review`, data);
      return response.data;
    } catch {
      return { success: false, message: 'Erro ao registar decisão do formulário.' };
    }
  }

  // Notes
  async updateLeadNote(leadId: number, noteId: number, data: { title: string; content: string; importance_level: string; visibility: string }): Promise<{ note: any }> {
    const response = await api.put(`/api/leads/${leadId}/notes/${noteId}`, data);
    return response.data;
  }
  async deleteLeadNote(leadId: number, noteId: number): Promise<void> {
    await api.delete(`/api/leads/${leadId}/notes/${noteId}`);
  }
  async highlightLeadNote(leadId: number, noteId: number, data: { is_highlighted: boolean }): Promise<{ note: any }> {
    const response = await api.post(`/api/leads/${leadId}/notes/${noteId}/highlight`, data);
    return response.data;
  }

  async getHighlightedNotes(leadId: number): Promise<{ highlighted_notes: any[] }> {
    const response = await api.get(`/api/leads/${leadId}/notes/highlighted`);
    return response.data;
  }

  // Vodafone Document Generation
  async getVodafoneTemplates(): Promise<VodafoneTemplate[]> {
    const response = await api.get('/api/documents/templates');
    return response.data.templates;
  }
  async getVodafoneTemplateFields(templateName: string): Promise<VodafoneField[]> {
    const response = await api.get(`/api/documents/fields/${templateName}`);
    return response.data.fields;
  }
  async previewVodafoneDocument(documentType: string, fieldData: Record<string, any>, leadId?: number): Promise<string> {
    const requestData: any = { document_type: documentType, field_data: fieldData };
    if (leadId) {
      requestData.lead_id = leadId;
    }
    const response = await api.post('/api/documents/preview', requestData);
    return response.data.pdf_data;
  }
  async generateDocument(leadId: number, documentType: string, fieldData: Record<string, any>): Promise<{ document: { id: number; download_url: string; preview_url: string; message: string } }> {
    const response = await api.post('/api/documents/generate', { lead_id: leadId, template_name: documentType, field_data: fieldData });
    return response.data;
  }

  async generateVodafoneDocument(leadId: number, documentType: string, fieldData: Record<string, any>): Promise<{ document: { id: number; download_url: string; preview_url: string; message: string } }> {
    return this.generateDocument(leadId, documentType, fieldData);
  }
  async downloadVodafoneDocument(documentId: number): Promise<Blob> {
    const response = await api.get(`/api/documents/${documentId}/download`, { responseType: 'blob' });
    return response.data;
  }

  // Lead Documents Management
  async getLeadDocuments(leadId: number): Promise<{ success: boolean; documents: VodafoneDocument[] }> {
    try {
      const response = await api.get(`/api/leads/${leadId}/documents`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async previewDocument(documentId: number): Promise<{ success: boolean; pdf_data: string } | { success: boolean; error: string; filename?: string; file_size?: number }> {
    try {
      const response = await api.get(`/api/documents/${documentId}/preview`);
      return response.data;
    } catch (error: any) {
      return { success: false, error: 'Erro ao carregar pré-visualização' };
    }
  }

  async downloadDocument(documentId: number): Promise<{ success: boolean; data: Blob }> {
    try {
      const response = await api.get(`/api/documents/${documentId}/download`, { responseType: 'blob' });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, data: new Blob() };
    }
  }

  // Document Progress Methods
  async getDocumentPages(documentName: string): Promise<{ success: boolean; pages: any[] }> {
    try {
      const response = await api.get(`/api/document-progress/pages/${encodeURIComponent(documentName)}`);
      return response.data;
    } catch (error: any) {
      return { success: false, pages: [] };
    }
  }

  async getFieldValues(leadId: number): Promise<{ success: boolean; field_values: Record<string, any> }> {
    try {
      const response = await api.get(`/api/document-progress/field-values/${leadId}`);
      return response.data;
    } catch (error: any) {
      return { success: false, field_values: {} };
    }
  }

  async saveFieldValues(leadId: number, fieldValues: Record<string, any>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/api/document-progress/field-values/${leadId}`, { field_values: fieldValues });
      return response.data;
    } catch (error: any) {
      return { success: false, message: 'Erro ao guardar valores dos campos' };
    }
  }

  async getDocumentProgress(leadId: number): Promise<{ success: boolean; progress: Record<string, any> }> {
    try {
      const response = await api.get(`/api/document-progress/progress/${leadId}`);
      return response.data;
    } catch (error: any) {
      return { success: false, progress: {} };
    }
  }

  async saveDocumentProgress(leadId: number, documentType: string, currentPage: number, isCompleted: boolean = false): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/api/document-progress/progress/${leadId}`, {
        document_type: documentType,
        current_page: currentPage,
        is_completed: isCompleted
      });
      return response.data;
    } catch (error: any) {
      return { success: false, message: 'Erro ao guardar progresso' };
    }
  }

  async getAutoFillSuggestions(leadId: number, fieldNames: string[]): Promise<{ success: boolean; suggestions: Record<string, any> }> {
    try {
      const response = await api.post(`/api/document-progress/auto-fill/${leadId}`, { field_names: fieldNames });
      return response.data;
    } catch (error: any) {
      return { success: false, suggestions: {} };
    }
  }

  async cleanupProgress(leadId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/api/document-progress/cleanup/${leadId}`);
      return response.data;
    } catch (error: any) {
      return { success: false, message: 'Erro ao limpar progresso' };
    }
  }

  // Leaderboard methods
  async getTopTenLeaderboard(): Promise<{ success: boolean; leaderboard: any[] }> {
    try {
      const response = await api.get('/api/leaderboard/top-ten');
      return response.data;
    } catch (error: any) {
      return { success: false, leaderboard: [] };
    }
  }

  async getAllScores(): Promise<{ success: boolean; scores: any[] }> {
    try {
      const response = await api.get('/api/leaderboard/all-scores');
      return response.data;
    } catch (error: any) {
      return { success: false, scores: [] };
    }
  }

  async updateUserScore(userId: number, installationCount: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/api/leaderboard/update-score', {
        user_id: userId,
        installation_count: installationCount
      });
      return response.data;
    } catch (error: any) {
      return { success: false, message: 'Erro ao atualizar pontuação' };
    }
  }

  async sendEmailWithAttachments({ subject, body, html, documentIds, files, destinatario }: { subject: string; body: string; html?: string; documentIds?: number[]; files?: File[]; destinatario?: string }): Promise<{ success: boolean; message: string }> {
    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('body', body);
    if (html) formData.append('html', html);
    if (destinatario) formData.append('destinatario', destinatario);
    if (documentIds) documentIds.forEach(id => formData.append('document_ids', id.toString()));
    if (files) files.forEach(file => formData.append('files', file));
    // Use the correct endpoint
    const response = await api.post('/api/send_email', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async submitReport(formData: FormData): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/api/submit-report', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // TaskMaster API methods
  async getTasks(params?: string): Promise<{ success: boolean; tasks: any[] }> {
    try {
      const url = params ? `/api/tasks?${params}` : '/api/tasks';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createTask(taskData: any): Promise<{ success: boolean; message: string; task?: any }> {
    try {
      const response = await api.post('/api/tasks', taskData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateTask(taskId: number, taskData: any): Promise<{ success: boolean; message: string; task?: any }> {
    try {
      const response = await api.put(`/api/tasks/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteTask(taskId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/api/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async completeTask(taskId: number, data: { response_text?: string; feedback?: string }): Promise<{ success: boolean; message: string; task?: any }> {
    try {
      const response = await api.post(`/api/tasks/${taskId}/complete`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTaskStatistics(): Promise<{ success: boolean; statistics: any }> {
    try {
      const response = await api.get('/api/tasks/statistics');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAvailableUsers(): Promise<{ success: boolean; users: any[] }> {
    try {
      const response = await api.get('/api/tasks/available-users');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Altice Sales
  async createAlticeSale(saleData: any): Promise<{ success: boolean; message: string; id?: number; data?: any }> {
    try {
      const response = await api.post('/api/altice-sales', saleData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAlticeSalesByLead(leadId: number): Promise<any[]> {
    try {
      const response = await api.get(`/api/altice-sales/lead/${leadId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAlticeSale(saleId: number): Promise<any> {
    try {
      const response = await api.get(`/api/altice-sales/${saleId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteAlticeSale(saleId: number): Promise<{ success: boolean; message?: string }> {
    try {
      await api.delete(`/api/altice-sales/${saleId}`);
      return { success: true, message: 'Formulário MEO eliminado.' };
    } catch (error) {
      throw error;
    }
  }

  // Solutio API methods - getUsersForSolutio (uses existing users endpoint)
  async getUsersForSolutio(): Promise<any> {
    try {
      const response = await api.get('/api/users');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getUserIncome(userId: number, year: number, month: number): Promise<any> {
    try {
      const response = await api.get(`/api/users/${userId}/income?year=${year}&month=${month}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getUserLeads(userId: number, year: number, month: number): Promise<any> {
    try {
      const response = await api.get(`/api/users/${userId}/leads?year=${year}&month=${month}&section=instalados`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAccountingStatus(userId: number, month: number, year: number, resolved: boolean): Promise<any> {
    try {
      const response = await api.put(`/api/admin/accounting-status`, {
        user_id: userId,
        month: month,
        year: year,
        resolved: resolved
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Product Resources API
  async getProductResources(): Promise<{
    telecomunicacao: Array<{ id: number; name: string; url: string; uploaded_at: string; uploaded_by: string; folder_id?: number | null }>;
    seguranca: Array<{ id: number; name: string; url: string; uploaded_at: string; uploaded_by: string; folder_id?: number | null }>;
    energia: Array<{ id: number; name: string; url: string; uploaded_at: string; uploaded_by: string; folder_id?: number | null }>;
  }> {
    const response = await api.get('/api/product-resources');
    return response.data;
  }

  // Analytics API
  async trackEvent(event: {
    event: string;
    category: string;
    action: string;
    label?: string;
    value?: number;
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      // TODO: Implement when backend is ready
      // await api.post('/api/analytics/track', event);
    } catch (error) {
      // Silently fail - analytics should not break the app
      console.warn('Failed to track event:', error);
    }
  }

  async uploadProductResource(formData: FormData): Promise<{ success: boolean; resource?: any; message?: string }> {
    const response = await api.post('/api/product-resources', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async deleteProductResource(resourceId: number): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.delete(`/api/product-resources/${resourceId}`);
      return response.data;
    } catch (error: any) {
      // Return error in consistent format
      if (error.response?.data) {
        return {
          success: false,
          error: error.response.data.error || error.response.data.message || 'Erro ao eliminar recurso',
          message: error.response.data.message || error.response.data.error
        };
      }
      throw error; // Re-throw if it's not an axios error
    }
  }

  // Resource Folders API
  async getResourceFolders(category?: string): Promise<{
    telecomunicacao: Array<{ id: number; name: string; category: string; parent_folder_id: number | null; created_at: string; created_by: string }>;
    seguranca: Array<{ id: number; name: string; category: string; parent_folder_id: number | null; created_at: string; created_by: string }>;
    energia: Array<{ id: number; name: string; category: string; parent_folder_id: number | null; created_at: string; created_by: string }>;
  }> {
    const params = category ? { category } : {};
    const response = await api.get('/api/product-resources/folders', { params });
    return response.data;
  }

  async createResourceFolder(name: string, category: string, parentFolderId?: number | null): Promise<{ success: boolean; folder?: any; message?: string; error?: string }> {
    try {
      const response = await api.post('/api/product-resources/folders', {
        name,
        category,
        parent_folder_id: parentFolderId || null
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return {
          success: false,
          error: error.response.data.error || error.response.data.message || 'Erro ao criar pasta',
          message: error.response.data.message || error.response.data.error
        };
      }
      throw error;
    }
  }

  async deleteResourceFolder(folderId: number): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.delete(`/api/product-resources/folders/${folderId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return {
          success: false,
          error: error.response.data.error || error.response.data.message || 'Erro ao eliminar pasta',
          message: error.response.data.message || error.response.data.error
        };
      }
      throw error;
    }
  }

  // Energy Proposal API
  async saveEnergyProposal(proposalData: {
    lead_id?: number | null;
    marcacao_id?: number | null;
    empresa: any;
    service_quantities: Record<string, number>;
    service_instances: any[];
    service_details: Record<string, any>;
  }): Promise<{ success: boolean; message: string; proposal_id?: number }> {
    try {
      const formData = new FormData();

      // Add JSON data
      formData.append('lead_id', proposalData.lead_id?.toString() || '');
      formData.append('marcacao_id', proposalData.marcacao_id?.toString() || '');
      formData.append('empresa', JSON.stringify(proposalData.empresa));
      formData.append('service_quantities', JSON.stringify(proposalData.service_quantities));
      formData.append('service_instances', JSON.stringify(proposalData.service_instances));

      // Process service_details to extract files
      const serviceDetailsForJson: Record<string, any> = {};
      const fileFields: Record<string, File> = {};

      for (const [instanceId, details] of Object.entries(proposalData.service_details)) {
        const processedDetails: any = { ...details };

        // Extract file fields and add to FormData
        for (const [field, value] of Object.entries(details)) {
          if (value instanceof File) {
            // Add file to FormData with a unique key
            const fileKey = `${instanceId}_${field}`;
            formData.append(fileKey, value);
            fileFields[fileKey] = value;
            // Store reference in JSON (just the field name, not the file)
            processedDetails[field] = fileKey;
          }
        }

        serviceDetailsForJson[instanceId] = processedDetails;
      }

      formData.append('service_details', JSON.stringify(serviceDetailsForJson));

      const response = await api.post('/api/energy-proposal/save', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getEnergyProposal(proposalId: number): Promise<any> {
    try {
      const response = await api.get(`/api/energy-proposal/${proposalId}`);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  async getEnergyProposalsByLead(leadId: number): Promise<any[]> {
    try {
      const response = await api.get(`/api/energy-proposal/lead/${leadId}`);
      // Handle both direct array and wrapped response
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching energy proposals:', error);
      return [];
    }
  }

  async downloadEnergyProposalFile(filePath: string, filename?: string): Promise<{ success: boolean; data: Blob }> {
    try {
      const response = await api.get(`/api/energy-proposal/file/download?path=${encodeURIComponent(filePath)}`, {
        responseType: 'blob'
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error downloading energy proposal file:', error);
      return { success: false, data: new Blob() };
    }
  }
}

// Create and export API service instance
const apiService = new ApiService();
export default apiService;


// Main page content API
export const getMainPageContent = async (): Promise<any> => {
  try {
    const response = await api.get('/api/main-page-content');
    return response.data;
  } catch (error) {
    return {
      important_news: '',
      main_news: '',
      ticker_news: [],
      cartoon_image: null,
      last_updated: null,
      updated_by: null
    };
  }
};

export const updateMainPageContent = async (data: {
  important_news?: string;
  main_news?: string;
  ticker_news?: string[];
  cartoon_image?: string;
}): Promise<any> => {
  try {
    const response = await api.post('/api/main-page-content', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// News System API
export interface NewsItem {
  id: number;
  title: string;
  description: string;
  category_tag: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  is_pinned: boolean;
  news_type: 'standard' | 'poll' | 'announcement';
  images?: NewsImage[];
  poll?: NewsPoll;
  reactions?: NewsReaction[];
  creator?: {
    id: number;
    username: string;
    avatar_url: string | null;
  };
  category?: NewsCategory;
}

export interface NewsCategory {
  id: number;
  tag: string;
  display_name: string;
  color: string | null;
  icon: string | null;
  created_at: string;
  is_active: boolean;
  created_by: number | null;
}

export interface NewsImage {
  id: number;
  news_item_id: number;
  image_url: string; // Base64 data URL (data:image/...;base64,...)
  filename: string | null;
  file_type: string | null;
  file_size: number | null;
  uploaded_at: string;
  order: number;
}

export interface NewsPoll {
  id: number;
  news_item_id: number;
  question: string;
  options: Array<{ id: string; text: string; vote_count: number }>;
  closes_at: string;
  allow_multiple_votes: boolean;
  show_results_live: boolean;
  created_at: string;
  is_closed: boolean;
  user_vote?: string[];
}

export interface NewsReaction {
  id: number;
  news_item_id: number;
  user_id: number;
  reaction_type: 'like' | 'love' | 'laugh' | 'wow' | 'sad' | 'angry';
  created_at: string;
}

export interface NewsPagination {
  page: number;
  per_page: number;
  total: number;
  pages: number;
}

// News API methods
export const getNews = async (params?: {
  category?: string;
  page?: number;
  per_page?: number;
  include_notifications?: boolean;
}): Promise<{ success: boolean; data: NewsItem[]; pagination: NewsPagination }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.include_notifications) queryParams.append('include_notifications', 'true');

    const response = await api.get(`/api/news?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createNews = async (data: {
  title: string;
  description?: string;
  category_tag?: string;
  is_pinned?: boolean;
  news_type?: 'standard' | 'poll' | 'announcement';
  poll?: {
    question: string;
    options: string[];
    closes_at: string;
    allow_multiple_votes?: boolean;
    show_results_live?: boolean;
  };
  create_notification?: boolean;
}): Promise<{ success: boolean; data: NewsItem }> => {
  try {
    const response = await api.post('/api/news', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateNews = async (newsId: number, data: {
  title?: string;
  description?: string;
  category_tag?: string;
  is_pinned?: boolean;
}): Promise<{ success: boolean; data: NewsItem }> => {
  try {
    const response = await api.put(`/api/news/${newsId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteNews = async (newsId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/api/news/${newsId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Categories API
export const getCategories = async (): Promise<{ success: boolean; data: NewsCategory[] }> => {
  try {
    const response = await api.get('/api/news/categories');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createCategory = async (data: {
  tag: string;
  display_name: string;
  color?: string;
  icon?: string;
}): Promise<{ success: boolean; data: NewsCategory }> => {
  try {
    const response = await api.post('/api/news/categories', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteCategory = async (categoryId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/api/news/categories/${categoryId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Polls API
export const votePoll = async (newsId: number, selectedOptions: string[]): Promise<{ success: boolean; data: NewsPoll }> => {
  try {
    const response = await api.post(`/api/news/${newsId}/poll/vote`, { selected_options: selectedOptions });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPoll = async (newsId: number): Promise<{ success: boolean; data: NewsPoll }> => {
  try {
    const response = await api.get(`/api/news/${newsId}/poll`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Reactions API
export const toggleReaction = async (newsId: number, reactionType: string): Promise<{ success: boolean; data: { reactions: Record<string, number>; user_reaction: string | null } }> => {
  try {
    const response = await api.post(`/api/news/${newsId}/reaction`, { reaction_type: reactionType });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getReactions = async (newsId: number): Promise<{ success: boolean; data: { reactions: Record<string, number>; total: number } }> => {
  try {
    const response = await api.get(`/api/news/${newsId}/reactions`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Images API
export const uploadNewsImage = async (newsId: number, imageFile: File, order?: number): Promise<{ success: boolean; data: NewsImage }> => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);
    if (order !== undefined) formData.append('order', order.toString());

    const response = await api.post(`/api/news/${newsId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteNewsImage = async (imageId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/api/news/images/${imageId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Profile API methods
export const getProfileUserInfo = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/user-info');
    return response.data.data; // Extract the actual user data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileCurrentIncome = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/income/current');
    return response.data.data; // Extract the actual data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileHistoricalIncome = async (months: number = 12): Promise<any> => {
  try {
    const response = await api.get(`/api/profile/income/historical?months=${months}`);
    return response.data.data; // Extract the actual data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileInstallationData = async (months: number = 12): Promise<any> => {
  try {
    const response = await api.get(`/api/profile/installation-data?months=${months}`);
    return response.data.data || response.data; // Extract the actual data from the response
  } catch (error) {
    console.error('Error fetching installation data:', error);
    throw error;
  }
};

export const getProfileInstallationChart = async (months: number = 12): Promise<any> => {
  try {
    const response = await api.get(`/api/profile/installation-chart?months=${months}`);
    return response.data.chart || response.data; // Extract chart from response
  } catch (error) {
    console.error('Error fetching installation chart:', error);
    throw error;
  }
};

// Team Point Configuration API
export const getTeamPointConfig = async (teamId: number): Promise<any> => {
  try {
    const response = await api.get(`/api/team-point-config/${teamId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateTeamPointConfig = async (teamId: number, config: any): Promise<any> => {
  try {
    const response = await api.put(`/api/team-point-config/${teamId}`, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUserTeamPointConfig = async (userId: number): Promise<any> => {
  try {
    const response = await api.get(`/api/team-point-config/user/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProfileBrackets = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/brackets');
    return response.data.data; // Extract the actual data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileRecommendations = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/recommendations');
    return response.data.data; // Extract the actual data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileSalesBrackets = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/sales-brackets');
    return response.data.data; // Extract the data property from the response
  } catch (error) {
    throw error;
  }
};

